import { z } from "zod";
import { DEMO_LIFECYCLE, DemoLifecycleSchema } from "../enums/demo";

export const DemoIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,62}$/);
export type DemoId = z.infer<typeof DemoIdSchema>;

const HostSchema = z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);
const EnvironmentKeySchema = z.string().regex(/^[A-Z][A-Z0-9_]*$/);

export const DemoIdentitySchema = z
  .object({ demoId: DemoIdSchema, incarnationId: z.uuid() })
  .strict();
export type DemoIdentity = z.infer<typeof DemoIdentitySchema>;

export const DemoDatabaseTargetSchema = z
  .object({
    projectId: z.string().min(1),
    branchId: z.string().min(1),
    databaseName: z.string().min(1),
    roleName: z.string().min(1),
    host: HostSchema,
    urlEnv: EnvironmentKeySchema,
  })
  .strict();
export type DemoDatabaseTarget = z.infer<typeof DemoDatabaseTargetSchema>;

export const DemoDefinitionSchema = z
  .object({
    id: DemoIdSchema,
    incarnationId: z.uuid(),
    browserHost: HostSchema,
    apiHosts: z.array(HostSchema),
    lifecycle: DemoLifecycleSchema,
    persistent: z.boolean(),
    release: z
      .object({ group: z.string().min(1), version: z.string().min(1) })
      .strict()
      .optional(),
    database: DemoDatabaseTargetSchema.nullable(),
    auth: z
      .object({
        adminSecretEnv: EnvironmentKeySchema,
        serviceSecretEnv: EnvironmentKeySchema,
        sessionSecretEnv: EnvironmentKeySchema,
      })
      .strict(),
    storage: z
      .object({ prefix: z.string().regex(/^(?:[a-z0-9-]+\/)*$/) })
      .strict(),
  })
  .strict()
  .superRefine((demo, ctx) => {
    if (
      (demo.lifecycle === DEMO_LIFECYCLE.READY ||
        demo.lifecycle === DEMO_LIFECYCLE.ACTIVE ||
        demo.lifecycle === DEMO_LIFECYCLE.DRAINING) &&
      demo.database === null
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["database"],
        message: "A ready demo requires a database target",
      });
    }
  });
export type DemoDefinition = z.infer<typeof DemoDefinitionSchema>;

export const DemoRegistrySchema = z
  .object({
    version: z.literal(1),
    primaryDemoId: DemoIdSchema,
    demos: z.array(DemoDefinitionSchema).min(1),
  })
  .strict()
  .superRefine((registry, ctx) => {
    const ids = new Set<string>();
    const hosts = new Set<string>();
    const prefixes = new Set<string>();
    const credentials = new Set<string>();
    const targets = new Set<string>();
    for (const [index, demo] of registry.demos.entries()) {
      const unique = (seen: Set<string>, value: string, field: string) => {
        if (seen.has(value))
          ctx.addIssue({
            code: "custom",
            path: ["demos", index, field],
            message: `Duplicate ${field}`,
          });
        seen.add(value);
      };
      unique(ids, demo.id, "id");
      for (const host of [demo.browserHost, ...demo.apiHosts])
        unique(hosts, host, "hostname");
      unique(prefixes, demo.storage.prefix, "storage.prefix");
      for (const key of Object.values(demo.auth))
        unique(credentials, key, "auth");
      if (demo.database) {
        unique(
          targets,
          `${demo.database.projectId}/${demo.database.branchId}/${demo.database.databaseName}`,
          "database",
        );
      }
    }
    if (!ids.has(registry.primaryDemoId))
      ctx.addIssue({
        code: "custom",
        path: ["primaryDemoId"],
        message: "Primary demo is not registered",
      });
  });
export type DemoRegistry = z.infer<typeof DemoRegistrySchema>;

export function demoDistinctId(demoId: DemoId): string {
  return `demo:${demoId}`;
}
